// Developer → Build tools.
//
// Release commands for the two things testers download: the Chrome extension
// and the desktop Exe agent. These moved off the tester dashboard — a tester
// downloads a build, they never make one.
//
// Everything here runs in a terminal at the repo root, so each command is
// copyable and each step says how you know it worked (the thing people
// actually get stuck on is not waiting for step 1 to finish).
import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Terminal, Copy, Check, Puzzle, Bot, Info, CheckCircle2, Clock, Upload, AlertTriangle,
} from "lucide-react";
import Aurora from "@/components/common/Aurora";
import { cn } from "@/lib/cn";

// Where each command must actually be run. The extension build is an npm script in
// frontend/package.json and there is no package.json at the repo root, so the page's
// old blanket "run everything from the repo root" made the one command it gives you
// fail with ENOENT. The backend's own 404 text says "in frontend/" for the same command.
const EXT_CMD = { cmd: "npm run build:ext:crx", cwd: "frontend/" };
const EXE_STEPS = [
  {
    cmd: ".\\.venv-agent\\Scripts\\Activate.ps1",
    cwd: "repo root",
    title: "Turn on the build environment",
    // The "how do I know it worked" line — this is the step people skip past.
    done: "Finishes instantly. You'll know it worked when (.venv-agent) appears at the start of your prompt line.",
    instant: true,
  },
  {
    cmd: ".\\backend\\qa_agent\\build_exe.ps1",
    cwd: "repo root",
    title: "Build and zip the exe",
    // build_exe.ps1 has FOUR stages. Stopping at the green BUILT line (stage 2)
    // leaves the zip unmade and nothing shipped, which is the failure this page
    // used to walk people straight into.
    done: "Takes a few minutes. The green BUILT: line is only stage 2 of 4 — keep waiting for stage 3/4 to write dist_agent/aiqa-agent-<version>.zip. That zip is the artifact you upload.",
    instant: false,
  },
];

function CopyCmd({ value, label }) {
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);
  const timer = useRef(null);

  // Clear on unmount and between clicks, or the previous 1.5s timer resets the
  // badge early and a late one fires against an unmounted component.
  useEffect(() => () => clearTimeout(timer.current), []);

  async function copy() {
    clearTimeout(timer.current);
    try {
      // navigator.clipboard is undefined on an insecure origin — a LAN dev server
      // over plain http is exactly that. The old `clipboard?.writeText(...)` silently
      // did nothing and still flipped the button to "Copied", so people pasted
      // whatever was already on their clipboard. Fall back, and never claim success.
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const ta = document.createElement("textarea");
        ta.value = value;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (!ok) throw new Error("copy rejected");
      }
      setCopied(true); setFailed(false);
    } catch {
      // A denied permission or an unfocused document rejects the promise; that
      // rejection used to go unhandled.
      setCopied(false); setFailed(true);
    }
    timer.current = setTimeout(() => { setCopied(false); setFailed(false); }, 1500);
  }

  return (
    <div className="mt-2 flex items-center gap-2">
      <code className="min-w-0 flex-1 overflow-x-auto whitespace-nowrap rounded-lg bg-white px-3 py-2 font-mono text-xs text-slate-800 dark:bg-ink-900 dark:text-slate-200">
        {value}
      </code>
      <button
        type="button"
        onClick={copy}
        // Three Copy buttons on this page otherwise share one accessible name.
        aria-label={label ? `Copy command: ${label}` : `Copy command ${value}`}
        className={cn(
          "inline-flex shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-2 text-xs font-medium transition-colors",
          failed
            ? "border-rose-300 text-rose-600 dark:border-rose-500/30 dark:text-rose-300"
            : "border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/5",
        )}
      >
        {failed
          ? <><AlertTriangle size={14} /> Select &amp; copy</>
          : copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
      </button>
    </div>
  );
}

// Where to run it — stated per command rather than once for the whole page.
function Cwd({ children }) {
  return (
    <p className="mt-2 text-[0.6875rem] font-medium uppercase tracking-wide text-slate-400">
      Run in <span className="font-mono normal-case text-slate-500 dark:text-slate-400">{children}</span>
    </p>
  );
}

export default function DevBuildTools() {
  return (
    <>
      {/* hero */}
      <motion.div
        initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="relative mb-6 overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-soft dark:border-white/10 dark:bg-ink-900 sm:p-7"
      >
        <Aurora className="opacity-60" />
        <div className="relative flex items-start gap-3">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-slate-700 to-slate-900 text-white shadow-glow">
            <Terminal size={20} />
          </span>
          <div>
            <p className="text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Release</p>
            <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-3xl">Build tools</h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Rebuild the Chrome extension and the desktop Exe agent after changing their
              source. Each command says where to run it — they are not all the same place.
              Windows PowerShell.
            </p>
          </div>
        </div>
      </motion.div>

      <div className="mb-5 flex items-start gap-2.5 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
        <Info size={16} className="mt-0.5 shrink-0 text-brand-500" />
        <p>
          These commands need the project source checked out locally. The two builds ship
          <strong> differently</strong> — the extension goes out over git, the exe over an
          upload. Each card says which.
        </p>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* Extension */}
        <div className="card p-5">
          <div className="flex items-start gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-brand-500/10 text-brand-600 dark:text-brand-300">
              <Puzzle size={18} />
            </span>
            <div className="min-w-0">
              <h2 className="font-semibold text-slate-900 dark:text-white">Chrome extension</h2>
              <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                One command — builds the bundle, signs the <code className="rounded bg-slate-100 px-1 dark:bg-white/10">.crx</code>, and
                writes the update manifest so testers auto-update.
              </p>
            </div>
          </div>

          <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/70 p-3 dark:border-white/10 dark:bg-white/5">
            <p className="text-xs font-semibold text-slate-600 dark:text-slate-300">
              Release a new extension version
            </p>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Runs this, then commit &amp; push. The
              <code className="mx-1 rounded bg-slate-100 px-1 dark:bg-white/10">version</code> in
              <code className="mx-1 rounded bg-slate-100 px-1 dark:bg-white/10">manifest.json</code> auto-bumps
              (patch) each build, so force-installed testers auto-update within a few hours.
            </p>
            <CopyCmd value={EXT_CMD.cmd} label="build the Chrome extension" />
            <Cwd>{EXT_CMD.cwd}</Cwd>
          </div>

          <p className="mt-3 flex items-start gap-1.5 text-xs text-slate-500 dark:text-slate-400">
            <CheckCircle2 size={13} className="mt-0.5 shrink-0 text-emerald-500" />
            Testers then get it from <strong className="mx-1">Extension Testing → System Health</strong>
            (version and size update automatically).
          </p>
        </div>

        {/* Exe agent */}
        <div className="card p-5">
          <div className="flex items-start gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-300">
              <Bot size={18} />
            </span>
            <div className="min-w-0">
              <h2 className="font-semibold text-slate-900 dark:text-white">Exe agent</h2>
              <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                Two steps, in order. Let step 1 finish before starting step 2.
              </p>
            </div>
          </div>

          <ol className="mt-4 space-y-3">
            {EXE_STEPS.map((s, i) => (
              <li key={s.cmd} className="rounded-xl border border-slate-200 bg-slate-50/70 p-3 dark:border-white/10 dark:bg-white/5">
                <p className="flex items-center gap-2 text-xs font-semibold text-slate-700 dark:text-slate-200">
                  <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-slate-900 text-[0.625rem] font-bold text-white dark:bg-white dark:text-slate-900">
                    {i + 1}
                  </span>
                  {s.title}
                </p>
                <CopyCmd value={s.cmd} label={s.title} />
                <Cwd>{s.cwd}</Cwd>
                <p className={cn(
                  "mt-2 flex items-start gap-1.5 text-xs",
                  s.instant ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400",
                )}>
                  {s.instant
                    ? <CheckCircle2 size={13} className="mt-0.5 shrink-0" />
                    : <Clock size={13} className="mt-0.5 shrink-0" />}
                  {s.done}
                </p>
              </li>
            ))}
          </ol>

          {/* The step the page used to leave out entirely. dist_agent/ is gitignored,
              so committing and pushing after an exe build ships NOTHING — the zip
              reaches testers only by being uploaded. Settings → Agent build is the
              route a developer can actually open; Exe Agent Testing is tester-only. */}
          <div className="mt-4 rounded-xl border border-brand-200 bg-brand-50/60 p-3 dark:border-brand-500/20 dark:bg-brand-500/10">
            <p className="flex items-center gap-2 text-xs font-semibold text-brand-700 dark:text-brand-200">
              <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-brand-600 text-[0.625rem] font-bold text-white">3</span>
              Ship it — this is what testers download
            </p>
            <p className="mt-1.5 flex items-start gap-1.5 text-xs text-brand-700/90 dark:text-brand-200/90">
              <Upload size={13} className="mt-0.5 shrink-0" />
              <span>
                Upload <code className="rounded bg-white/70 px-1 font-mono dark:bg-white/10">dist_agent/aiqa-agent-&lt;version&gt;.zip</code> at{" "}
                <strong>Settings → Agent build</strong>. Do <strong>not</strong> commit &amp; push for the
                exe — <code className="rounded bg-white/70 px-1 font-mono dark:bg-white/10">dist_agent/</code> is
                gitignored, so pushing ships nothing and testers keep the old agent.
              </span>
            </p>
          </div>

          <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
            In short: run <strong>1</strong>, wait for <code className="rounded bg-slate-100 px-1 py-0.5 font-mono dark:bg-white/10">(.venv-agent)</code> to
            appear → run <strong>2</strong>, wait for the <strong>zip</strong> at stage 4/4 → then
            upload it.
          </p>
        </div>
      </div>
    </>
  );
}
